/**
 * App监控日志 - 枚举定义
 *
 * @description 定义App监控日志相关的枚举类型
 * @module types/app-logs/enums
 * @author YSFL Team
 * @date 2026-01-20
 */

/**
 * 平台类型
 *
 * platform 语义为“运行渠道/容器”而非 OS（OS 由 os_version 表达）。
 * android/ios 仅表示 App 壳（Android/iOS 原生容器）。
 */
export type Platform =
  | 'android'
  | 'ios'
  | 'h5'
  | 'h5-dingtalk'
  | 'h5-wecom'
  | 'mp-weixin'
  | 'mp-alipay'
  | 'mp-dingtalk'

/**
 * 性能指标类型
 */
export type PerformanceMetricType =
  | 'page_load'
  | 'api_request'
  | 'scan'
  | 'bluetooth'
  | 'data_operation'
  | 'memory'

/**
 * 错误类型
 */
export type ErrorType =
  | 'js_error'
  | 'api_error'
  | 'business_error'
  | 'scan_error'
  | 'bluetooth_error'
  | 'data_error'
  | 'session_crash'
  | 'native_crash'

/**
 * 错误级别
 */
export type AppErrorLevel = 'fatal' | 'error' | 'warning' | 'info'
/** @deprecated 请使用 AppErrorLevel */
export type ErrorLevel = AppErrorLevel

/**
 * 影响级别
 */
export type ImpactLevel = 'critical' | 'high' | 'medium' | 'low'

/**
 * 行为类型
 */
export type ActionType =
  | 'page_view'
  | 'button_click'
  | 'form_submit'
  | 'scan'
  | 'search'

/**
 * 操作结果
 */
export type ActionResult = 'success' | 'failed' | 'cancelled' | 'pending'

/**
 * 平台类型标签
 */
export const PLATFORM_LABELS: Record<Platform, string> = {
  android: 'Android',
  ios: 'iOS',
  h5: 'H5',
  'h5-dingtalk': '钉钉 H5',
  'h5-wecom': '企业微信 H5',
  'mp-weixin': '微信小程序',
  'mp-alipay': '支付宝小程序',
  'mp-dingtalk': '钉钉小程序',
}

/**
 * 性能指标类型标签
 */
export const METRIC_TYPE_LABELS: Record<PerformanceMetricType, string> = {
  page_load: '页面加载',
  api_request: 'API请求',
  scan: '扫码',
  bluetooth: '蓝牙',
  data_operation: '数据操作',
  memory: '内存',
}

/**
 * 错误类型标签
 */
export const ERROR_TYPE_LABELS: Record<ErrorType, string> = {
  js_error: 'JS错误',
  api_error: 'API错误',
  business_error: '业务错误',
  scan_error: '扫码错误',
  bluetooth_error: '蓝牙错误',
  data_error: '数据错误',
  session_crash: '会话异常终止',
  native_crash: '原生崩溃',
}

/**
 * 错误级别标签（App监控专用）
 */
export const APP_ERROR_LEVEL_LABELS: Record<AppErrorLevel, string> = {
  fatal: '致命',
  error: '错误',
  warning: '警告',
  info: '信息',
}

/**
 * 影响级别标签
 */
export const IMPACT_LEVEL_LABELS: Record<ImpactLevel, string> = {
  critical: '严重',
  high: '高',
  medium: '中',
  low: '低',
}

/**
 * 行为类型标签
 */
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  page_view: '页面浏览',
  button_click: '按钮点击',
  form_submit: '表单提交',
  scan: '扫码',
  search: '搜索',
}

/**
 * 操作结果标签
 */
export const ACTION_RESULT_LABELS: Record<ActionResult, string> = {
  success: '成功',
  failed: '失败',
  cancelled: '取消',
  pending: '等待',
}

